import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Upload, Loader2, FileText, Trash2 } from 'lucide-react';
import { uploadImage, deleteTempFiles, UploadError } from '../../lib/r2';
import { ImageViewer } from './ImageViewer';
import { PdfViewer } from './PdfViewer';
import { generatePdfThumbnail, isPdfFile } from '../../lib/pdfUtils';
import { useTranslations } from '../../i18n';
import { processImageFile } from '../../lib/imageUtils';
import { AlertModal } from './AlertModal';
import { LoadingModal } from './LoadingModal';
import { ConfirmModal } from './ConfirmModal';

interface ImageWithComment {
  file?: File;
  url: string;
  comment?: string;
  thumbnail?: string;
  sizeBytes?: number;
  is_receipt?: boolean;
  log_entry_id?: string;
  note_id?: string;
}

interface ImageUploadProps {
  images: ImageWithComment[];
  onChange: (images: ImageWithComment[]) => void;
  folder: string;
  maxImages?: number;
  showComments?: boolean;
  readOnly?: boolean;
  label?: string;
  type?: 'car' | 'expense' | 'part' | 'note';
  itemId?: string;
  hideUploadButton?: boolean;
  onUploadingChange?: (uploading: boolean) => void;
  carId?: string;
  showReceiptToggle?: boolean;
  horizontal?: boolean;
}

export interface ImageUploadHandle {
  openFileDialog: () => void;
}

export const ImageUpload = forwardRef<ImageUploadHandle, ImageUploadProps>(({ images, onChange, folder, maxImages = 10, showComments = false, readOnly = false, label, type, itemId, hideUploadButton = false, onUploadingChange, carId, showReceiptToggle = false, horizontal = false }, ref) => {
  const [uploading, setUploading] = useState(false);
  const { t } = useTranslations();
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [alertConfig, setAlertConfig] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });

  const generateSessionId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  };

  const uploadSessionId = useRef(generateSessionId());

  useImperativeHandle(ref, () => ({
    openFileDialog: () => {
      if (images.length >= maxImages) {
        setAlertConfig({
          isOpen: true,
          title: t.common.error,
          message: (t.common as any).fileCountLimit?.replace('{limit}', maxImages.toString()) || `Limit reached: ${maxImages} files`
        });
        return;
      }
      fileInputRef.current?.click();
    }
  }));

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);


  const processingThumbs = useRef(new Set());

  useEffect(() => {
    const syncThumbnails = async () => {
      for (const image of images) {
        if (image.thumbnail && !thumbnails[image.url]) {
          setThumbnails(prev => ({ ...prev, [image.url]: image.thumbnail || '' }));
        }
      }
    };
    syncThumbnails();
    handleLazyThumbnails();
  }, [images]);

  const handleLazyThumbnails = async () => {
    const pdfsToThumb = images.filter(img => {
      const isPdf = isPdfFile(img.url);
      const thumbIsActuallyPdf = img.thumbnail && isPdfFile(img.thumbnail);
      const needsThumb = isPdf && (!img.thumbnail || thumbIsActuallyPdf);
      const alreadyProcessing = processingThumbs.current.has(img.url);


      return needsThumb && !alreadyProcessing;
    });
    if (pdfsToThumb.length === 0) {

      return;
    }



    for (const img of pdfsToThumb) {
      if (processingThumbs.current.has(img.url)) continue;
      processingThumbs.current.add(img.url);

      try {

        const thumbDataUrl = await generatePdfThumbnail(img.url);
        const thumbBlob = dataURLtoBlob(thumbDataUrl);

        const emailWorkerDomain = 'workers.dev'; // Relaxed check
        if (!img.url.includes(emailWorkerDomain)) {
          console.warn(`⚠️[ImageUpload] Skipping ${img.url} - not a worker URL`);
          continue;
        }

        const fileMarker = '/file/';
        const markerIndex = img.url.indexOf(fileMarker);
        if (markerIndex === -1) {
          console.warn(`⚠️[ImageUpload] Skipping ${img.url} - no / file / marker`);
          continue;
        }

        const storagePath = decodeURIComponent(img.url.substring(markerIndex + fileMarker.length));
        const finalWorkerBase = img.url.substring(0, markerIndex);


        const formData = new FormData();
        formData.append('storagePath', storagePath);
        formData.append('thumb', thumbBlob, 'thumb.jpg');

        const uploadRes = await fetch(`${finalWorkerBase}/upload-thumb`, {
          method: 'POST',
          body: formData
        });

        if (uploadRes.ok) {
          const { thumbUrl } = await uploadRes.json();
          // Find the expense_images ID if possible, but ImageUpload works with URLs.
          // In AddExpenseForm, images are linked by URL.
          // We update local thumbnails state immediately
          setThumbnails(prev => ({ ...prev, [img.url]: thumbUrl }));

          // Also try to update Supabase if we have itemId (which is log_entry_id)
          if (itemId && type === 'expense') {
            const { supabase } = await import('../../lib/supabase');
            await supabase
              .from('log_entry_images')
              .update({ image_url_thumb: thumbUrl })
              .eq('log_entry_id', itemId)
              .eq('image_url', img.url);
          }

        }
      } catch (err) {
        console.error('❌ [ImageUpload] LazyThumb failed:', err);
      } finally {
        processingThumbs.current.delete(img.url);
      }
    }
  };

  const generateImageThumbnail = async (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_SIZE = 400;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas to Blob failed'));
        }, 'image/jpeg', 0.8);
        URL.revokeObjectURL(img.src);
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const dataURLtoBlob = (dataurl: string) => {
    const arr = dataurl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) return new Blob();
    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const remainingSlots = maxImages - images.length;
    
    if (files.length > remainingSlots) {
      setAlertConfig({
        isOpen: true,
        title: t.common.error,
        message: (t.common as any).fileCountLimit?.replace('{limit}', maxImages.toString()) || `Only ${remainingSlots} more files allowed (Limit: ${maxImages})`
      });
    }

    const filesToUpload = files.slice(0, remainingSlots);
    if (filesToUpload.length === 0) {
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const uploadedImages: ImageWithComment[] = [];

      for (let file of filesToUpload) {
        let thumbBlob: Blob | undefined;
        let thumbDataUrl: string | undefined;

        file = await processImageFile(file);

        try {
          if (isPdfFile(file.name)) {
            const fileUrl = URL.createObjectURL(file);
            thumbDataUrl = await generatePdfThumbnail(fileUrl);
            thumbBlob = dataURLtoBlob(thumbDataUrl);
            URL.revokeObjectURL(fileUrl);
          } else if (file.type.startsWith('image/')) {
            thumbBlob = await generateImageThumbnail(file);
            thumbDataUrl = URL.createObjectURL(thumbBlob);
          }
        } catch (err) {
          console.warn('Failed to generate local thumbnail:', err);
        }

        const result = await uploadImage(file, folder, type, itemId, thumbBlob, uploadSessionId.current, carId);
        uploadedImages.push({
          url: result.imageUrl,
          comment: '',
          thumbnail: result.thumbUrl || thumbDataUrl,
          sizeBytes: result.sizeBytes,
          is_receipt: false
        });
      }

      onChange([...uploadedImages, ...images]);
    } catch (error) {
      console.error('Upload failed:', error);
      let errorMessage = t.common.uploadFailed || 'File upload failed';
      if (error instanceof UploadError && error.code) {
        if (error.code === 'FILE_TOO_LARGE') {
          errorMessage = (t.common as any).fileTooLarge?.replace('{limit}', error.limit?.toString() || '0') || errorMessage;
        } else if (error.code === 'STORAGE_FULL') {
          errorMessage = (t.common as any).storageFull?.replace('{limit}', error.limit?.toString() || '0') || errorMessage;
        } else if (error.code === 'DAILY_UPLOAD_LIMIT') {
          errorMessage = (t.common as any).dailyUploadLimit?.replace('{limit}', error.limit?.toString() || '0') || errorMessage;
        } else if (error.code === 'HOURLY_UPLOAD_LIMIT') {
          errorMessage = (t.common as any).hourlyUploadLimit?.replace('{limit}', error.limit?.toString() || '0') || errorMessage;
        } else if (error.code === 'FILE_COUNT_LIMIT') {
          errorMessage = (t.common as any).fileCountLimit?.replace('{limit}', error.limit?.toString() || '0') || errorMessage;
        }
      }
      setAlertConfig({ isOpen: true, title: t.common.error, message: errorMessage });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const handleRemove = (index: number) => {
    if (readOnly) return;
    setDeleteIndex(index);
  };

  const confirmRemove = () => {
    if (deleteIndex === null) return;
    const index = deleteIndex;
    const image = images[index];
    if (image?.url && image.url.startsWith('https://')) {
      deleteTempFiles([image.url]);
    }
    onChange(images.filter((_, i) => i !== index));
    setDeleteIndex(null);
  };

  const handleCommentChange = (index: number, comment: string) => {
    if (readOnly) return;
    const updated = [...images];
    updated[index].comment = comment;
    onChange(updated);
  };

  const handleReceiptToggle = (index: number, isReceipt: boolean) => {
    if (readOnly) return;
    const updated = [...images];
    updated[index].is_receipt = isReceipt;
    onChange(updated);
  };

  const canAddMore = !readOnly; // Always show if not read-only, handle limit via alert on click

  const handleImageClick = (image: ImageWithComment, index: number) => {
    if (isPdfFile(image.url)) {
      setPdfViewerUrl(image.url);
    } else {
      setViewerIndex(index);
    }
  };

  return (
    <>
      <div className={showComments ? "space-y-4" : horizontal ? "flex overflow-x-auto gap-4 pb-2" : "flex flex-wrap gap-4"}>
        {/* If horizontal, show Add button first */}
        {horizontal && canAddMore && !hideUploadButton && (
          <label className={`flex-shrink-0 flex flex-col items-center justify-center border-2 border-dashed border-gray-200 dark:border-white/10 rounded-xl cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50/30 dark:hover:bg-blue-400/5 transition-all w-20 h-20`}>
            {uploading ? (
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            ) : (
              <>
                <Upload size={18} className="text-gray-400 mb-1" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 text-center px-2">
                  {label ? label.replace((t.common as any).add + ' ', '').replace('Add ', '') : ((t.common as any).add || 'Add')}
                </span>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
          </label>
        )}

        {images.map((image, index) => {
          const isPdf = isPdfFile(image.url);
          // Only treat as thumbnail if it's not a PDF itself

          if (showComments) {
            return (
              <div key={index} className="w-full flex gap-2 p-2 bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-xl shadow-sm transition-all group overflow-hidden">
                <div className="relative flex-shrink-0">
                  {!isPdf || thumbnails[image.url] ? (
                    <img
                      src={thumbnails[image.url] || image.url}
                      alt={`Upload ${index + 1}`}
                      className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => handleImageClick(image, index)}
                    />
                  ) : (
                    <div
                      className="w-20 h-20 flex items-center justify-center bg-gray-50 dark:bg-slate-900/40 rounded-lg cursor-pointer transition-colors"
                      onClick={() => handleImageClick(image, index)}
                    >
                      <FileText size={24} className="text-gray-400" />
                    </div>
                  )}
                  {isPdf && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded font-semibold shadow">
                      PDF
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0 flex flex-col">
                  <textarea
                    value={image.comment || ''}
                    onChange={(e) => handleCommentChange(index, e.target.value)}
                    placeholder={(t.gallery as any).commentPlaceholder || 'Image comment'}
                    className="w-full flex-1 px-3 py-1.5 bg-gray-50 dark:bg-slate-900/20 border border-gray-100 dark:border-white/10 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-slate-900/20 disabled:text-gray-500 min-w-0 text-sm"
                    rows={2}
                    disabled={readOnly}
                  />

                  {showReceiptToggle && (
                    <div className="mt-2 flex items-center">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={image.is_receipt || false}
                          onChange={(e) => handleReceiptToggle(index, e.target.checked)}
                          disabled={readOnly}
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-500"></div>
                        <span className="ml-2 text-xs font-medium text-gray-600 dark:text-gray-400">
                          {t.gallery.isReceipt || "This is a receipt"}
                        </span>
                      </label>
                    </div>
                  )}
                </div>

                {!readOnly && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(index);
                    }}
                    className="p-1 px-1 text-red-500 hover:text-red-600 transition-all flex-shrink-0 self-center"
                    title={(t.gallery as any).deleteImageTitle || "Delete file"}
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            );
          }

          // Grid view for no comments
          return (
            <div key={index} className={`relative flex-shrink-0 p-1 bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl shadow-sm ${horizontal ? 'flex-shrink-0' : ''}`}>
              {!isPdf || thumbnails[image.url] ? (
                <img
                  src={thumbnails[image.url] || image.url}
                  alt={`Upload ${index + 1}`}
                  className="w-20 h-20 object-cover rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => handleImageClick(image, index)}
                />
              ) : (
                <div
                  className="w-20 h-20 flex items-center justify-center bg-gray-50 dark:bg-slate-900/40 rounded-xl cursor-pointer transition-colors"
                  onClick={() => handleImageClick(image, index)}
                >
                  <FileText size={24} className="text-gray-300 dark:text-gray-600" />
                </div>
              )}
              {isPdf && (
                <div className="absolute top-3 right-3 bg-red-500 text-white text-xs px-2 py-1 rounded font-semibold shadow">
                  PDF
                </div>
              )}
              {!readOnly && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(index);
                  }}
                  className="absolute -top-1 -right-1 text-red-500 hover:text-red-600 transition-all z-10 flex items-center justify-center"
                  title="Delete image"
                >
                  <Trash2 size={18} />
                </button>
              )}
            </div>
          );
        })}

        {!horizontal && canAddMore && !hideUploadButton && (
          <label className={`flex flex-col items-center justify-center border-2 border-dashed border-gray-200 dark:border-white/10 rounded-xl cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50/30 dark:hover:bg-blue-400/5 transition-all ${showComments ? "w-full h-16" : "w-20 h-20"
            }`}>
            {uploading ? (
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            ) : (
              <>
                <Upload size={18} className="text-gray-400 mb-1" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 text-center px-2">
                  {label ? label.replace((t.common as any).add + ' ', '').replace('Add ', '') : ((t.common as any).add || 'Add')}
                </span>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
          </label>
        )}

        {/* Hidden input for external triggering when button is hidden */}
        {canAddMore && hideUploadButton && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,application/pdf"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
        )}
      </div>

      {viewerIndex !== null && (
        <ImageViewer
          images={images.filter(img => !isPdfFile(img.url)).map(img => img.url)}
          initialIndex={viewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      )}

      {pdfViewerUrl && (
        <PdfViewer
          url={pdfViewerUrl}
          onClose={() => setPdfViewerUrl(null)}
        />
      )}

      <AlertModal
        isOpen={alertConfig.isOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmLabel={t.common.done}
        onClose={() => setAlertConfig(prev => ({ ...prev, isOpen: false }))}
      />

      <ConfirmModal
        isOpen={deleteIndex !== null}
        title={t.gallery?.deleteImageTitle || "Dzēst attēlu"}
        message={t.gallery?.deleteImageConfirm || "Vai tiešām vēlaties dzēst šo attēlu?"}
        confirmLabel={t.common?.delete || "Dzēst"}
        cancelLabel={t.common?.cancel || "Atcelt"}
        onConfirm={confirmRemove}
        onCancel={() => setDeleteIndex(null)}
        onClose={() => setDeleteIndex(null)}
        isDestructive={true}
      />

      <LoadingModal
        isOpen={uploading}
        title={(t.common as any).uploading || 'Augšupielādē failu...'}
        message={(t.common as any).pleaseWait || 'Lūdzu uzgaidiet'}
      />
    </>
  );
});
